import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import { useShell } from "./useShell";
import { useSession } from "@/features/auth/useSession";
import { useCustomDomainBranding } from "./useCustomDomainBranding";
import { SENTINELS_THEME } from "./theme.constants";
import type { BrandingTheme, ThemeContextValue } from "./theme.types";
import { hasPlaneCapability } from "@/features/auth/planeAuthorization";
import { useEffectivePresentation } from "@/features/admin/presentation/api";

/* ── Constants ──────────────────────────────────────────── */

/** Default favicon path shipped with the static build. */
const DEFAULT_FAVICON = "/vite.svg";

/** Default document title. */
const DEFAULT_TITLE = "Sentinels Hub";

/* ── Custom domain detection ────────────────────────────── */

const PRIMARY_DOMAIN =
  import.meta.env.VITE_PRIMARY_DOMAIN ?? "core.sentinels.pro";
const LOCALHOST_HOSTS = new Set(["localhost", "127.0.0.1"]);

/** True when the browser is on a custom domain (not the primary platform). */
export function isCustomDomainHost(): boolean {
  const host = window.location.hostname.toLowerCase();
  if (host === PRIMARY_DOMAIN) return false;
  if (LOCALHOST_HOSTS.has(host)) return false;
  return true;
}

/** Return the custom domain hostname, or undefined if on the primary domain. */
export function getCustomDomainHost(): string | undefined {
  return isCustomDomainHost()
    ? window.location.hostname.toLowerCase()
    : undefined;
}

/* ── Context ────────────────────────────────────────────── */

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Access the current branding theme. Must be used inside ThemeProvider. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}

/* ── Helpers ────────────────────────────────────────────── */

/**
 * Transform backend branding_config into a flat BrandingTheme.
 *
 * Backend format:
 * ```json
 * { "theme": { "sidebar_bg": "...", ... }, "logos": { ... }, "meta": { ... } }
 * ```
 *
 * Returns null if the config is empty or malformed.
 */
function parseBrandingConfig(
  config: Record<string, unknown> | null | undefined,
): BrandingTheme | null {
  if (!config || typeof config !== "object") return null;

  const theme = config.theme as Record<string, string> | undefined;
  if (!theme || typeof theme !== "object" || !theme.sidebar_bg) return null;

  const logos = (config.logos ?? {}) as Record<string, string>;
  const meta = (config.meta ?? {}) as Record<string, string>;

  return {
    sidebar_bg: theme.sidebar_bg,
    sidebar_text: theme.sidebar_text ?? "#FFFFFF",
    sidebar_active_bg: theme.sidebar_active_bg ?? "rgba(255,255,255,0.12)",
    sidebar_active_indicator:
      theme.sidebar_active_indicator ?? theme.sidebar_bg,
    accent_primary: theme.accent_primary ?? theme.sidebar_bg,
    accent_secondary: theme.accent_secondary ?? theme.sidebar_bg,
    brand_primary: theme.brand_primary ?? theme.sidebar_bg,
    logos: {
      sidebar: logos.sidebar,
      sidebar_client: logos.sidebar_client,
      header: logos.header,
      favicon: logos.favicon,
    },
    meta: {
      app_title: meta.app_title,
      support_email: meta.support_email,
    },
  };
}

/* ── CSS Variable injection ─────────────────────────────── */

function applyThemeCssVars(theme: BrandingTheme): void {
  const root = document.documentElement;
  root.style.setProperty("--theme-sidebar-bg", theme.sidebar_bg);
  root.style.setProperty("--theme-sidebar-text", theme.sidebar_text);
  root.style.setProperty("--theme-sidebar-active-bg", theme.sidebar_active_bg);
  root.style.setProperty(
    "--theme-sidebar-active-indicator",
    theme.sidebar_active_indicator,
  );
  root.style.setProperty("--theme-accent-primary", theme.accent_primary);
  root.style.setProperty("--theme-accent-secondary", theme.accent_secondary);
  root.style.setProperty("--theme-brand-primary", theme.brand_primary);
}

/** Convert only the closed server-governed token vocabulary into UI branding. */
export function parseGovernedTheme(
  tokens: Record<string, string | number> | null | undefined,
): BrandingTheme | null {
  if (!tokens) return null;
  const color = (key: string): string | undefined => {
    const value = tokens[key];
    return typeof value === "string" &&
      /^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/i.test(value)
      ? value
      : undefined;
  };
  const sidebar = color("color.sidebar_background");
  const sidebarText = color("color.sidebar_text");
  const accent = color("color.accent_primary");
  const brand = color("color.brand_primary");
  if (!sidebar || !sidebarText || !accent || !brand) return null;
  return {
    sidebar_bg: sidebar,
    sidebar_text: sidebarText,
    sidebar_active_bg:
      color("color.sidebar_active_background") ??
      SENTINELS_THEME.sidebar_active_bg,
    sidebar_active_indicator: color("color.sidebar_active_indicator") ?? accent,
    accent_primary: accent,
    accent_secondary: color("color.accent_secondary") ?? accent,
    brand_primary: brand,
    logos: {},
    meta: {},
  };
}

/* ── Provider ───────────────────────────────────────────── */

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { activeShell } = useShell();
  const { session } = useSession();
  const presentationPlane = activeShell === "admin" ? "command" : "frontier";
  const mayReadPresentation = hasPlaneCapability(
    session?.authorization,
    presentationPlane,
    `${presentationPlane}.presentation.read`,
  );
  const { data: effectivePresentation } = useEffectivePresentation(
    presentationPlane,
    !!session && mayReadPresentation,
  );
  const governedTheme = useMemo(
    () => parseGovernedTheme(effectivePresentation?.theme.tokens),
    [effectivePresentation],
  );

  // Detect custom domain context
  const customDomain = useMemo(() => getCustomDomainHost(), []);
  const onCustomDomain = !!customDomain;

  // Fetch branding from API when on a custom domain (pre-auth, no session needed)
  const { data: domainBranding } = useCustomDomainBranding(customDomain);

  // Resolve branding_config from the active organization in bootstrap data
  const orgBranding = useMemo(() => {
    if (!session) return null;

    const activeOrgId = session.active_organization?.id;
    if (!activeOrgId) return null;

    const org = session.organizations.find((o) => o.id === activeOrgId);
    if (!org) return null;

    return {
      theme: parseBrandingConfig(org.branding_config),
      name: org.name,
    };
  }, [session]);

  // Resolve domain-based branding (used before session is available)
  const domainBrandingResolved = useMemo(() => {
    if (!domainBranding) return null;
    return {
      theme: parseBrandingConfig(domainBranding.branding_config),
      name: domainBranding.name,
    };
  }, [domainBranding]);

  // Shell-aware theme resolution:
  // - admin shell → always Sentinels default
  // - custom domain → domain branding or session org branding
  // - ops/client shell → org branding if available, else Sentinels default
  const resolved = useMemo((): ThemeContextValue => {
    if (governedTheme) {
      return {
        theme: governedTheme,
        isOrgTheme: effectivePresentation?.source !== "platform_default",
        isCustomDomain: onCustomDomain,
        orgName: session?.active_organization?.name,
      };
    }

    if (activeShell === "admin") {
      return {
        theme: SENTINELS_THEME,
        isOrgTheme: false,
        isCustomDomain: onCustomDomain,
        orgName: undefined,
      };
    }

    // Prefer session org branding, fall back to domain-based branding
    const branding = orgBranding ?? domainBrandingResolved;

    if (!branding?.theme) {
      return {
        theme: SENTINELS_THEME,
        isOrgTheme: false,
        isCustomDomain: onCustomDomain,
        orgName: undefined,
      };
    }

    return {
      theme: branding.theme,
      isOrgTheme: true,
      isCustomDomain: onCustomDomain,
      orgName: branding.name,
    };
  }, [
    activeShell,
    domainBrandingResolved,
    effectivePresentation?.source,
    governedTheme,
    onCustomDomain,
    orgBranding,
    session?.active_organization?.name,
  ]);

  // Inject CSS custom properties on :root whenever theme changes
  useEffect(() => {
    applyThemeCssVars(resolved.theme);
  }, [resolved.theme]);

  // ── WP 1.2: Dynamic document title ──────────────────────
  // Priority: custom app_title > "Sentinels Hub · OrgName" > "Sentinels Hub"
  // Admin shell always shows the default title regardless of org.
  useEffect(() => {
    if (activeShell === "admin") {
      document.title = DEFAULT_TITLE;
      return;
    }

    const customTitle = resolved.theme.meta.app_title;
    if (customTitle && customTitle !== DEFAULT_TITLE) {
      document.title = customTitle;
    } else if (resolved.orgName) {
      document.title = `${DEFAULT_TITLE} \u00B7 ${resolved.orgName}`;
    } else {
      document.title = DEFAULT_TITLE;
    }
  }, [activeShell, resolved.theme.meta.app_title, resolved.orgName]);

  // ── WP 1.1: Dynamic favicon ─────────────────────────────
  // When the active theme provides a favicon URL, swap the <link rel="icon">
  // href. Revert to the default on unmount or when the URL is empty/404.
  const faviconRevertRef = useRef<string>(DEFAULT_FAVICON);

  const revertFavicon = useCallback(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) {
      link.href = faviconRevertRef.current;
    }
  }, []);

  useEffect(() => {
    const faviconUrl = resolved.theme.logos.favicon;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;

    // Capture the original href on first run so we can restore it later.
    if (faviconRevertRef.current === DEFAULT_FAVICON && link.href) {
      faviconRevertRef.current = link.href;
    }

    if (!faviconUrl) {
      // No custom favicon — ensure we show the default
      revertFavicon();
      return;
    }

    // Probe the URL before applying it so a 404 never flashes a broken icon.
    const img = new Image();
    let cancelled = false;

    img.onload = () => {
      if (!cancelled) {
        link.href = faviconUrl;
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        revertFavicon();
      }
    };
    img.src = faviconUrl;

    return () => {
      cancelled = true;
      revertFavicon();
    };
  }, [resolved.theme.logos.favicon, revertFavicon]);

  return <ThemeContext value={resolved}>{children}</ThemeContext>;
}
